import { useMemo, type ReactNode } from 'react';
import { ApolloProvider } from '@apollo/client/react';
import type { ApolloClient } from '@apollo/client';
import { createApolloClient } from '@/lib/apollo/client';
import { SessionProvider } from '@/app/providers/SessionProvider';
import {
    ExternalNavigationProvider,
    type ExternalNavigator,
} from '@/app/providers/ExternalNavigationProvider';

interface AppProvidersProps {
    children: ReactNode;
    /** Injected by tests to supply a mocked client. Production passes nothing. */
    client?: ApolloClient;
    /** Injected by tests to supply a mocked external navigator. Production passes nothing. */
    navigator?: ExternalNavigator;
}

/**
 * Every cross-cutting provider the app needs, in one place.
 *
 * Tests render this with their own `client`/`navigator`, so a test exercises
 * the same provider tree as production rather than a hand-rolled
 * approximation.
 */
export function AppProviders({
    children,
    client,
    navigator,
}: Readonly<AppProvidersProps>) {
    const apolloClient = useMemo(
        () => client ?? createApolloClient(),
        [client]
    );

    return (
        <ApolloProvider client={apolloClient}>
            <SessionProvider>
                <ExternalNavigationProvider navigator={navigator}>
                    {children}
                </ExternalNavigationProvider>
            </SessionProvider>
        </ApolloProvider>
    );
}
